import { useQuery } from '@tanstack/react-query';
import type { UserDto } from '../generated/model';
import {
  getGetUsersQueryKey,
  getGetUsersQueryOptions,
  putWhoami,
  useGetUsers,
  useGetUsersSuspense,
} from '../generated/user-controller/user-controller.ts';
import { queryClient } from '../queryClient.ts';

const USERS_CACHE = { staleTime: Infinity, gcTime: Infinity } as const;
const CURRENT_USER_CACHE = { staleTime: Infinity, gcTime: Infinity } as const;
const CURRENT_USER_QUERY_KEY = ['/api/users/whoami'] as const;

function addCurrentUserToUsersCache(currentUser: UserDto) {
  queryClient.setQueryData<UserDto[]>(getGetUsersQueryKey(), (users) => {
    if (!users || users.some((user) => user.id === currentUser.id)) {
      return users;
    }

    return [...users, currentUser];
  });
}

export async function getCurrentUser() {
  const currentUser = await putWhoami();
  addCurrentUserToUsersCache(currentUser);

  return currentUser;
}

export function useCurrentUser(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: CURRENT_USER_QUERY_KEY,
    queryFn: getCurrentUser,
    ...CURRENT_USER_CACHE,
    enabled: options?.enabled,
  });
}

export function useUsers(options?: { enabled?: boolean }) {
  return useGetUsers({
    query: {
      ...USERS_CACHE,
      enabled: options?.enabled,
    },
  });
}

export function useUsersSuspense() {
  return useGetUsersSuspense({
    query: {
      ...USERS_CACHE,
    },
  });
}

export function prefetchUsers() {
  return queryClient.prefetchQuery(
    getGetUsersQueryOptions({
      query: {
        ...USERS_CACHE,
      },
    }),
  );
}
